
import { NgRedux } from "@angular-redux/store";
import { Injectable } from "@angular/core";
import { fromJS } from "immutable";
import { memoize } from "lodash";
import { Action } from "redux";
import { Observable } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";
import { GenericActions } from "./generic-actions";
import { mergeReducers } from "./root.reducer";

@Injectable()
export class ImmutableRedux<State> {
    public select = memoize(this._select, (...args) => args.join());
    public _store: State = this.ngRedux["_store"];

    constructor(public ngRedux: NgRedux<any>) { }

    public dispatch(action): Action {
        return this.ngRedux.dispatch(action);
    }

    public dispatchFactory(actionType, rootProp, stateProp?, payload?, extraProps?): Action {
        const type = `${rootProp}::${actionType}`;
        return this.dispatch({ type, payload, stateProp, ...extraProps });
    }

    public getState(rootProp, compProp?): State {
        const existingState = this.ngRedux.getState();
        if (existingState[rootProp] && existingState[rootProp].toJS) {
            if (!!compProp && existingState[rootProp].get(compProp) !== undefined) {
                return existingState[rootProp].get(compProp).toJS();
            } else {
                return existingState[rootProp].toJS();
            }
        } else {
            if (!!compProp) {
                return existingState[rootProp][compProp];
            } else {
                return existingState[rootProp];
            }
        }
    }

    public addComponentTo(rootProp, stateProp, initialState, useExisting) {
        this.dispatchFactory(GenericActions.COMPONENT_ADDED,
            rootProp,
            stateProp,
            fromJS(initialState),
            {useExisting},
        );
    }

    public registerComponent(newReducer, rootProp, stateProp, initialState, useExisting = false) {
        if (this.getState(rootProp)) {
            this.addComponentTo(
                rootProp,
                stateProp,
                initialState,
                useExisting,
            );
        } else {
            mergeReducers(this.ngRedux, {
                [rootProp]: newReducer(rootProp, fromJS({ [stateProp]: initialState })),
            });
        }
    }

    public unregisterComponent(rootProp, stateProp) {
        this.dispatchFactory(GenericActions.COMPONENT_REMOVED, rootProp, stateProp);
    }

    public checkState(rootProp, prop?: string) {
        if (prop) {
            return this.ngRedux.getState()[rootProp] && this.ngRedux.getState()[rootProp].toJS()[prop];
        } else {
            return this.ngRedux.getState()[rootProp];
        }
    }

    private statesAreDefined(stateA, stateB) {
        return stateA !== undefined && stateB !== undefined && stateA.equals !== undefined;
    }

    private dataConversion(data) {
        let state: any = data.state;
        const compProp: any = data.compProp;
        if (state && state.toJS) {
            state = state.toJS();
            if (compProp) {
                state = state[compProp];
            }
        }
        return state;
    };

    private _select(prop: string, compProp?: string): Observable<State> {
        return this.ngRedux.select(prop).pipe(
            distinctUntilChanged((stateA: any, stateB: any) => {
                if (this.statesAreDefined(stateA, stateB) && !!compProp) {
                    return stateA.get(compProp) && stateA.get(compProp).equals(stateB.get(compProp));
                } else if (this.statesAreDefined(stateA, stateB) && !compProp) {
                    return stateA.equals(stateB);
                } else {
                    return false;
                }
            }),
            map((state) => ({ state, compProp })),
            map(this.dataConversion),
            filter((state) => state !== undefined));
    }
}
